<template>
  <div>
    <div v-if="displayLoader">
      <div class="text-center">
        <div>
          <b-spinner label="Spinning"></b-spinner>
        </div>
        <h2 class="pt-5">Loading report. It may take a few seconds.</h2>
      </div>
    </div>
    <NotFoundPage v-else-if="displayNotFound" title="Report not found" />
    <NotFoundPage
      v-else-if="isHidePrices"
      title="Report is not available"
      subtitle="Report is not available for current user due to system settings. Please contact our administrator for elaboration"
    />
    <div v-else>
      <AssignmentStripe />
      <div class="container">
        <div class="report-container">
          <ReportHeader />
          <div class="optimization-wrap">
            <ReportSummary />
            <BeforeOptimizationTable />
            <AfterOptimizationTable />
          </div>
        </div>

        <div class="assignment-footer d-flex justify-content-between align-items-center">
          <div class="footer-btn ml-auto d-flex align-items-center">
            <AssignmentActions origin="report" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { Assignment } from '@/types/Models/Assignment'
import { BSpinner } from 'bootstrap-vue-next'
import { defineComponent, computed, type ComputedRef, onMounted } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { LoadingStatus } from '@/const/LoadingStatus'
import { useAccountStore } from '@/store/account'
import { useAssignmentSummaryStore } from '@/store/assignmentSummary'
import { useOptimizationReportStore } from '@/store/optimizationReport'
import NotFoundPage from '@/views/Components/NotFoundPage/NotFound.vue'
import ReportHeader from '@/views/Assignments/Report/Header.vue'
import ReportSummary from '@/views/Assignments/Report/Summary/Summary.vue'
import AssignmentStripe from '@/views/Components/Assignment/Stripe/AssignmentStripe.vue'
import AssignmentActions from '@/views/Components/Assignment/Actions/AssignmentActions.vue'
import AfterOptimizationTable from '@/views/Assignments/Report/After.vue'
import BeforeOptimizationTable from '@/views/Assignments/Report/Before.vue'

export default defineComponent({
  name: 'OptimizationReport',
  components: {
    BSpinner,
    NotFoundPage,
    ReportHeader,
    ReportSummary,
    AssignmentStripe,
    AssignmentActions,
    AfterOptimizationTable,
    BeforeOptimizationTable,
  },
  setup() {
    const accountStore = useAccountStore()
    const assignmentSummaryStore = useAssignmentSummaryStore()
    const optimizationReportStore = useOptimizationReportStore()

    const isStaff: ComputedRef<boolean> = computed(() => accountStore.isStaff)
    const assignment: ComputedRef<Assignment> = computed(() => assignmentSummaryStore.assignment as Assignment)
    const loadingStatus: ComputedRef<LoadingStatus> = computed(() => optimizationReportStore.status)
    const displayLoader: ComputedRef<boolean> = computed(() =>
      [LoadingStatus.PENDING, LoadingStatus.LOADING].includes(loadingStatus.value),
    )
    const displayNotFound: ComputedRef<boolean> = computed(() => loadingStatus.value === LoadingStatus.FAILURE)
    const isHidePrices: ComputedRef<boolean> = computed(() => !isStaff.value && assignment.value?.company?.hidePrice)

    onMounted(() => {
      optimizationReportStore.fetch(assignment.value.id)
    })

    onBeforeRouteLeave((_to, _from) => {
      optimizationReportStore.reset()
      return undefined
    })

    return {
      isHidePrices,
      displayLoader,
      displayNotFound,
    }
  },
})
</script>
